import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreateCustomTemplateForm } from '@/forms/CreateCustomTemplateForm';
import { createStoryTemplate } from '@/api/services/story-templates/createStoryTemplate';
import type {
  CreateCustomTemplateFormValues,
  CreateStoryTemplateRequest,
} from 'schemas';
import { type ApiError } from '@/api/ApiError';
import { createCustomTemplatePageMessages } from './createCustomTemplatePage.messages';
import { ROUTES } from '@/routes';
import { Heading2 } from '@/components/Typography';
import { toast } from 'sonner';

export function CreateCustomTemplatePage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (formData: CreateCustomTemplateFormValues) => {
    setIsSubmitting(true);

    try {
      // Apply fallback defaults for fields not shown in the form
      const storyTemplateData: CreateStoryTemplateRequest = {
        ...formData,
        ageRange: '3-6',
        storyThemes: ['custom'],
        keyEvents: [],
        settingDetails: '',
      };

      const result = await createStoryTemplate(storyTemplateData);
      if (result?.storyTemplate) {
        toast.success(createCustomTemplatePageMessages.templateCreated);
        // Navigate to create story wizard with the created template ID
        navigate(ROUTES.CREATE_STORY(result.storyTemplate.id));
      } else {
        toast.error(createCustomTemplatePageMessages.unexpectedError);
      }
    } catch (error: unknown) {
      // createStoryTemplate ensures the error will be an ApiError
      toast.error((error as ApiError).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(ROUTES.DASHBOARD_STORY_TEMPLATES);
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      <div className="mb-6 flex justify-center">
        <img
          src="/logo/wfsc-logo-with-flair-xlong.png"
          alt=""
          className="h-20 w-auto"
        />
      </div>
      <div className="rounded-lg border border-stone-200 bg-white p-8 shadow-sm">
        {/* Header */}
        <div className="mb-8">
          <Heading2 className="mb-4">
            {createCustomTemplatePageMessages.pageTitle}
          </Heading2>
        </div>

        {/* Form Content */}
        <div
          aria-label={createCustomTemplatePageMessages.formContainerAriaLabel}
        >
          <CreateCustomTemplateForm
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            onCancel={handleCancel}
          />
        </div>
      </div>
    </div>
  );
}
